import type { ReactNode } from "react";
import type { IconComponentType } from "@/components/base/badges/badge-types";
import { cx } from "@/utils/cx";

export type LabelSize = "sm" | "md" | "lg";
export type LabelType = "default" | "light" | "dark";

export interface LabelProps {
	/** The text content to display */
	children: ReactNode;
	/** Size variant — sm (12px), md (14px), lg (16px) */
	size?: LabelSize;
	/**
	 * Color/weight variant:
	 * - default: medium-emphasis, extrabold at sm, semibold at md/lg
	 * - light: low-emphasis, semibold across all sizes
	 * - dark: high-emphasis, extrabold across all sizes
	 */
	type?: LabelType;
	/** Optional icon rendered before the text */
	icon?: IconComponentType;
	/** Whether to render the icon — only meaningful when `icon` is provided */
	showIcon?: boolean;
	className?: string;
}

const sizeConfig: Record<
	LabelSize,
	{ textSize: string; iconSize: string; lineHeight: string }
> = {
	sm: {
		textSize: "text-xs",
		iconSize: "size-3.5",
		lineHeight: "leading-[18px]",
	},
	md: {
		textSize: "text-sm",
		iconSize: "size-3.5",
		lineHeight: "leading-5",
	},
	lg: {
		textSize: "text-md",
		iconSize: "size-4",
		lineHeight: "leading-6",
	},
};

const colorConfig: Record<LabelType, string> = {
	default: "text-secondary",
	light: "text-quaternary",
	dark: "text-primary",
};

/**
 * Returns true when the size+type combination should use the display
 * (extrabold) font. All other combinations use the body (semibold) font.
 *
 * Rules from Figma:
 *  - dark  → always extrabold
 *  - default + sm → extrabold
 *  - default + md/lg, light (any size) → semibold
 */
function isDisplayVariant(size: LabelSize, type: LabelType): boolean {
	return type === "dark" || (type === "default" && size === "sm");
}

export function Label({
	children,
	size = "md",
	type = "default",
	icon: Icon,
	showIcon = true,
	className,
}: LabelProps) {
	const { textSize, iconSize, lineHeight } = sizeConfig[size];
	const colorClass = colorConfig[type];
	const display = isDisplayVariant(size, type);

	// Letter-spacing only applies for extrabold at the xs (12 px) size
	const hasTracking = display && size === "sm";

	return (
		<div
			className={cx(
				// overflow-x only — truncate/overflow-hidden clips descenders (g/y/p)
				"flex items-center gap-1 overflow-x-clip overflow-y-visible",
				className,
			)}
		>
			{showIcon && Icon && (
				<Icon className={cx("shrink-0", iconSize, colorClass)} />
			)}
			<span
				className={cx(
					"min-w-0 overflow-x-clip overflow-y-visible text-ellipsis whitespace-nowrap",
					textSize,
					lineHeight,
					colorClass,
					display ? "font-display font-extrabold" : "font-body font-semibold",
					hasTracking && "tracking-[0.24px]",
				)}
			>
				{children}
			</span>
		</div>
	);
}
